import { 
  Box, 
  Tab, 
  Button, 
  Typography, 
  FormLabel, 
  TextField, 
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
} from '@mui/material';
import TabPanel from '@mui/lab/TabPanel';
import { styled, alpha } from '@mui/material/styles';

export const PageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  backgroundColor: theme.palette.white.main,
  borderRadius: theme.palette.custom.borderRadius,
}));

export const CustomTab = styled(Tab)(({ theme }) => ({
  fontSize: '1.1rem',
  fontWeight: '600',
  textTransform: 'none',
  color: theme.palette.grey[600],
  '&.Mui-selected': {
    color: theme.palette.primary.main,
  },
}));

export const PageHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

export const PageTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.2rem',
  fontWeight: 600,
  color: theme.palette.custom.sidebarText,
}));

export const AddWorkerButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.main, 
  borderRadius: '8px', 
  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
  color: theme.palette.primary.main, 
  fontSize: '1.1rem', 
  fontWeight: '600',
  textTransform: 'none', 
  padding: '6px 25px', 
  '&:hover': {
    backgroundColor: theme.palette.primary.light, 
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)', 
  },
  
  '&:active': {
    boxShadow: 'none', 
    backgroundColor: theme.palette.primary.main,
  },
  
  '& .MuiButton-startIcon': {
    marginRight: '8px',
  },
}));

export const Label = styled(FormLabel)(({ theme }) => ({
  fontSize: '0.85rem', // Reduced from 1.1rem
  fontWeight: '500',
  color: theme.palette.grey[600],
  marginBottom: theme.spacing(0.5), // Reduced from 1
}));

export const CustomTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.white.main,
    height: '36px', // Forced high-density height
    '& input': {
      padding: '8px 12px',
      fontSize: '0.9rem',
    },
    '& fieldset': {
      borderColor: theme.palette.grey[300],
    },
    '&.Mui-focused fieldset': {
      borderWidth: '1px',
    },
  },
}));

export const CustomTabPanel = styled(TabPanel)(({ theme }) => ({
  padding: theme.spacing(4, 0),
}));

export const InvitationContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[200],
  borderRadius: theme.palette.custom.borderRadius,
  padding: theme.spacing(3),
  marginTop: theme.spacing(3),
}));

export const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  padding: 0,
  marginRight: theme.spacing(1), 
  color: theme.palette.success.main,
  '&.Mui-checked': {
    color: theme.palette.success.main,
  },
}));

export const ButtonGroupContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2), 
  justifyContent: 'flex-end',
  marginTop: theme.spacing(5),
}));

export const CreateProfileButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontWeight: 'bold',
  textTransform: 'none',
  padding: theme.spacing(1, 3),
  borderRadius: theme.spacing(1),
  minWidth: '200px',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    boxShadow: 'none',
  },
}));

export const CancelButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: theme.palette.primary.main,
  fontWeight: 'bold',
  textTransform: 'none',
  padding: theme.spacing(1, 3),
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.primary.main}`,
  minWidth: '200px',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: alpha(theme.palette.error.main, 0.1),
    boxShadow: 'none',
  },
  textAlign: 'center',
}));

export const ErrorMessage = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: '0.75rem',
  marginTop: theme.spacing(0.5),
}));

export const StyledTableContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.white.main,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.grey[200]}`,
  overflowX: 'auto',
  boxShadow: 'none',
}));

export const StyledTable = styled(Table)({
  minWidth: 800,
  borderCollapse: 'separate',
  borderSpacing: 0,
});

export const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: theme.palette.primary.light, 
  
  '& th': {
    backgroundColor: 'inherit',
    color: theme.palette.custom.sidebarText,
    fontWeight: 600,
    fontSize: '0.85rem',
    borderBottom: 'none',
    padding: theme.spacing(1.5, 2),
  },
}));

export const StyledTableBody = styled(TableBody)(({ theme }) => ({
  '& tr:nth-of-type(odd)': {
    backgroundColor: theme.palette.white.main, // White rows
  },
  '& tr:nth-of-type(even)': {
    backgroundColor: theme.palette.primary.light, // Very light grey stripe
  },
  '& tr:last-child td': {
    borderBottom: 0,
  },
}));

export const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: '0.85rem',
  color: theme.palette.custom.sidebarText,
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  padding: theme.spacing(1.5, 2),
  display: 'table-cell',
  verticalAlign: 'middle',
}));

export const PaginationFooter = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingTop: theme.spacing(2), 
  color: theme.palette.grey[600],
  fontSize: '0.85rem',
  paddingBottom: 0,
}));

interface StatusTagProps {
  status: 'Active' | 'Pending' | 'Blocked' | string;
}

export const InvitationStatusTag = styled(Box)<StatusTagProps>(({ theme, status }) => {
  let bgColor: string;
  let color: string;

  switch (status) {
    case 'pending':
      bgColor = alpha(theme.palette.warning.light, 0.2);
      color = theme.palette.warning.dark
      break;
    case 'invitation_sent':
      bgColor = alpha(theme.palette.link.main, 0.2);
      color = theme.palette.link.main;
      break;
    default:
      bgColor = theme.palette.grey[100];
      color = theme.palette.grey[600];
  }

  return {
    backgroundColor: bgColor,
    color: color,
    fontWeight: 600,
    fontSize: '0.75rem',
    padding: theme.spacing(0.5, 1.5),
    borderRadius: '4px',
    textAlign: 'center',
    width: 'fit-content',
  };
});

export const InvitationAction = styled(Box)<StatusTagProps>(({ theme, status }) => {
  let bgColor: string;
  let color: string;

  switch (status) {
    case 'pending':
      bgColor = alpha(theme.palette.primary.dark, 0.2);
      color = theme.palette.primary.dark
      break;
    case 'invitation_sent':
      bgColor = alpha(theme.palette.link.main, 0.2);
      color = theme.palette.link.main;
      break;
    default:
      bgColor = theme.palette.grey[100];
      color = theme.palette.grey[600];
  }

  return {
    backgroundColor: "transparent",
    color: color,
    fontWeight: 600,
    fontSize: '0.75rem',
    padding: theme.spacing(0.5, 1.5),
    borderRadius: '4px',
    textAlign: 'center',
    width: 'fit-content',
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: bgColor,
    }
  };
});

export const SearchContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: theme.palette.grey[100],
  borderRadius: theme.spacing(1),
  padding: theme.spacing(0.5, 2),
  width: '300px',
  border: `1px solid ${theme.palette.grey[300]}`,
  '&:focus-within': {
    border: `1px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.white.main,
  },
}));

export const SearchInput = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      border: 'none',
    },
    '& input': {
      padding: theme.spacing(1, 0),
      fontSize: '0.9rem',
    },
  },
}));